import React from 'react';
import { CheckCircle2, XCircle, Circle, AlertTriangle } from 'lucide-react';

// Pipeline stages in order
const PIPELINE_STAGES = [
  { key: 'submitted',         label: 'Applied' },
  { key: 'eligibility',       label: 'Eligibility Check' },
  { key: 'review',            label: 'Under Review' },
  { key: 'shortlisted',       label: 'Shortlisted' },
  { key: 'test_invited',      label: 'Assessment Invited' },
  { key: 'test_completed',    label: 'Assessment Completed' },
  { key: 'final_decision',    label: 'Final Decision' },
  { key: 'offer_sent',        label: 'Offer Sent' },
  { key: 'spot_confirmed',    label: 'Spot Confirmed' },
  { key: 'enrolled',          label: 'Enrolled' },
];

// Map application status → which stage is current + any terminal state
function getStageState(app) {
  const status = app.status;

  // Terminal rejection states
  if (status === 'ineligible') {
    return {
      completedUpTo: 'eligibility',
      terminal: { after: 'eligibility', label: 'Ineligible', reason: app.ineligibility_reason },
      current: null,
    };
  }
  if (status === 'not_shortlisted') {
    return {
      completedUpTo: 'review',
      terminal: { after: 'review', label: 'Not Shortlisted', reason: 'Admin review decision' },
      current: null,
    };
  }
  if (status === 'not_selected') {
    return {
      completedUpTo: 'test_completed',
      terminal: { after: 'test_completed', label: 'Not Selected', reason: 'Final evaluation decision' },
      current: null,
    };
  }
  if (status === 'rejected' || status === 'test_failed') {
    return {
      completedUpTo: 'test_completed',
      terminal: { after: 'test_completed', label: 'Not Selected', reason: 'Final evaluation decision' },
      current: null,
    };
  }

  // Active states
  const stageMap = {
    received:       app.shortlisted === true
                      ? { completedUpTo: 'review', current: 'shortlisted' }
                      : { completedUpTo: 'eligibility', current: 'review' },
    shortlisted:    { completedUpTo: 'review', current: 'shortlisted' },
    test_invited:   { completedUpTo: 'shortlisted', current: 'test_invited' },
    test_completed: { completedUpTo: 'test_invited', current: 'test_completed' },
    accepted:       app.acceptance_confirmed
                      ? { completedUpTo: 'spot_confirmed', current: 'enrolled' }
                      : app.acceptance_offer_sent
                        ? { completedUpTo: 'offer_sent', current: 'spot_confirmed' }
                        : { completedUpTo: 'final_decision', current: 'offer_sent' },
    on_hold:        { completedUpTo: 'test_completed', current: 'final_decision' },
    enrolled:       { completedUpTo: 'enrolled', current: null },
  };

  return stageMap[status] || { completedUpTo: 'submitted', current: 'eligibility' };
}

// Get date for a stage from events array
function getStageDate(events, stageKey) {
  const eventMap = {
    submitted:      ['submitted'],
    eligibility:    ['submitted'],
    review:         ['shortlisted', 'not_shortlisted'],
    shortlisted:    ['shortlisted'],
    test_invited:   ['test_invited'],
    test_completed: ['test_completed'],
    final_decision: ['allocated', 'outcome_email_sent', 'not_selected'],
    offer_sent:     ['acceptance_email_sent'],
    spot_confirmed: ['acceptance_confirmed'],
    enrolled:       ['enrolled'],
  };
  const types = eventMap[stageKey] || [];
  const event = events?.find(e => types.includes(e.event_type));
  if (!event) return null;
  try {
    return new Date(event.created_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
  } catch {
    return null;
  }
}

// Map stage key → status value for override calls
const STAGE_TO_STATUS = {
  review:         'received',
  shortlisted:    'shortlisted',
  test_invited:   'test_invited',
  test_completed: 'test_completed',
  final_decision: 'accepted',
  enrolled:       'enrolled',
};

export default function ApplicationStagePath({ app, events = [], emailLogs = [], onStageClick }) {
  if (!app) return null;

  const offerSent = emailLogs.some(e => e.template_key === 'acceptance' && e.status === 'sent');
  const appWithOffer = { ...app, acceptance_offer_sent: offerSent };
  const { completedUpTo, current, terminal } = getStageState(appWithOffer);
  const completedIdx = PIPELINE_STAGES.findIndex(s => s.key === completedUpTo);
  const currentIdx = current ? PIPELINE_STAGES.findIndex(s => s.key === current) : -1;

  // Was the application overridden?
  const isOverridden = !!app.override_reason;

  return (
    <div className="w-full">
      {/* Override banner */}
      {isOverridden && (
        <div className="flex items-start gap-2 p-2.5 mb-3 rounded-lg bg-amber-50 border border-amber-200">
          <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-xs text-amber-800">
            <span className="font-semibold">Manual override</span> by {app.override_by}
            {app.override_reason && ` — ${app.override_reason}`}
          </div>
        </div>
      )}

      {/* Stage path */}
      <div className="flex items-start overflow-x-auto pb-1 gap-0">
        {PIPELINE_STAGES.map((stage, i) => {
          const isPast = i <= completedIdx;
          const isCurrent = stage.key === current;
          const isTerminalHere = terminal && terminal.after === stage.key;
          const isClickable = onStageClick && STAGE_TO_STATUS[stage.key] && !isCurrent;

          // Date label
          const dateStr = isPast || isCurrent ? getStageDate(events, stage.key) : null;

          const handleClick = () => {
            if (isClickable) onStageClick({ stageKey: stage.key, stageLabel: stage.label, targetStatus: STAGE_TO_STATUS[stage.key] });
          };

          return (
            <React.Fragment key={stage.key}>
              <div
                className={`flex flex-col items-center flex-shrink-0 min-w-[72px] max-w-[88px] ${isClickable ? 'cursor-pointer group' : ''}`}
                onClick={handleClick}
                title={isClickable ? `Move to: ${stage.label}` : undefined}
              >
                {/* Icon */}
                <div className={`w-7 h-7 rounded-full flex items-center justify-center flex-shrink-0 border-2 transition-all
                  ${isPast && !isCurrent ? 'bg-green-500 border-green-500' : ''}
                  ${isCurrent ? 'bg-primary border-primary shadow-md ring-2 ring-primary/20' : ''}
                  ${!isPast && !isCurrent ? 'bg-white border-gray-200' : ''}
                  ${isClickable ? 'group-hover:ring-2 group-hover:ring-orange-400 group-hover:ring-offset-1' : ''}
                `}>
                  {isPast && !isCurrent ? (
                    <CheckCircle2 className="w-4 h-4 text-white" />
                  ) : isCurrent ? (
                    <div className="w-2.5 h-2.5 rounded-full bg-white" />
                  ) : (
                    <Circle className="w-3 h-3 text-gray-300" />
                  )}
                </div>

                {/* Label */}
                <span className={`text-[10px] font-medium text-center mt-1.5 leading-tight transition-colors
                  ${isPast && !isCurrent ? 'text-green-700' : ''}
                  ${isCurrent ? 'text-primary font-semibold' : ''}
                  ${!isPast && !isCurrent ? 'text-gray-400' : ''}
                  ${isClickable ? 'group-hover:text-orange-500' : ''}
                `}>
                  {stage.label}
                </span>

                {/* Date */}
                {dateStr && (
                  <span className="text-[9px] text-muted-foreground mt-0.5 text-center leading-tight">{dateStr}</span>
                )}
              </div>

              {/* Terminal marker (rejection point) */}
              {isTerminalHere && (
                <div className="flex flex-col items-center flex-shrink-0 min-w-[72px] max-w-[88px]">
                  <div className="w-px h-7 bg-gray-200 mt-0" style={{ marginTop: '4px' }} />
                  <div className="w-7 h-7 rounded-full flex items-center justify-center bg-red-100 border-2 border-red-300 mt-0">
                    <XCircle className="w-4 h-4 text-red-500" />
                  </div>
                  <span className="text-[10px] font-semibold text-red-600 text-center mt-1.5 leading-tight">
                    {terminal.label}
                  </span>
                  {terminal.reason && (
                    <span className="text-[9px] text-red-400 mt-0.5 text-center leading-tight px-1">{terminal.reason}</span>
                  )}
                </div>
              )}

              {/* Connector line */}
              {i < PIPELINE_STAGES.length - 1 && !isTerminalHere && (
                <div className={`flex-1 h-0.5 mt-3.5 min-w-[12px]
                  ${i < completedIdx ? 'bg-green-400' : 'bg-gray-200'}
                `} />
              )}
            </React.Fragment>
          );
        })}
      </div>
    </div>
  );
}
